import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * GoalProgressBadge — compact list cell for a student's (or halaqa's)
 * memorization goal progress: "{percent}%" next to an on-track / behind-plan
 * pill, or a muted "No goal set" pill when there is no goal.
 *
 * Pure presentation: callers pass the already-derived `percent` and `state`
 * (see src/utils/quranStatus.js → planState / groupPlanState) so this component
 * stays reusable across GroupsPage and StudentsPage.
 *
 * state ∈ 'onTrack' | 'behindPlan' | 'noGoal'
 */

const PLAN_COLORS = {
  onTrack: { bg: 'var(--color-success-light, #ECFDF5)', fg: 'var(--color-success-dark, #15803D)' },
  behindPlan: { bg: 'var(--color-danger-light, #FEF2F2)', fg: 'var(--color-danger-dark, #B91C1C)' },
  noGoal: { bg: 'var(--color-surface-alt, #f3f4f6)', fg: 'var(--color-text-secondary, #6b7280)' },
};

const pillStyle = (c) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: '0.6875rem',
  fontWeight: 600,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  background: c.bg,
  color: c.fg,
});

export default function GoalProgressBadge({ percent = 0, state = 'noGoal' }) {
  const { t } = useTranslation();

  if (state === 'noGoal') {
    return <span style={pillStyle(PLAN_COLORS.noGoal)}>{t('quran.noGoalSet', { defaultValue: 'No goal set' })}</span>;
  }

  const isBehind = state === 'behindPlan';
  const c = isBehind ? PLAN_COLORS.behindPlan : PLAN_COLORS.onTrack;
  const label = isBehind
    ? t('quran.behindPlan', { defaultValue: 'Behind plan' })
    : t('quran.onTrack', { defaultValue: 'On track' });
  const safePercent = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{
        fontWeight: 700,
        fontSize: '0.8125rem',
        fontVariantNumeric: 'tabular-nums',
        color: 'var(--color-text-primary, #1A1F36)',
      }}>
        {safePercent}%
      </span>
      <span style={pillStyle(c)}>{label}</span>
    </div>
  );
}
